import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, Inject, PLATFORM_ID, ChangeDetectorRef } from '@angular/core';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import { isPlatformBrowser } from '@angular/common';
import { of, forkJoin } from 'rxjs';
import { catchError, finalize, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { SharedService } from 'src/app/modules/shared/_services/shared.service';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-trending-section',
  templateUrl: './trending-section.component.html',
  styleUrls: ['./trending-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TrendingSectionComponent implements OnInit, OnDestroy {
  trendingStates: any[] = [];
  trendingCities: any[] = [];
  trendingKeywords: any[] = [];
  trendingAuthorities: any[] = [];
  trendingSearchList: any[] = [];
  baseUrl: string = environment.TENDER.BASE_URL;
  formattedValue: string = '';
  isLoading = false;
  type: string = '';
  authorityState: string = '';
  stateformatedauthority: string = '';

  private destroy$ = new Subject<void>();
  private isBrowser: boolean;

  constructor(
    private transferState: TransferState,
    private router: Router,
    private sharedService: SharedService,
    private cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    this.parseUrlAndLoadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private parseUrlAndLoadData(): void {
    const urlSegments = this.router.url.split('/');

    if (urlSegments.length <= 2) {
      return;
    }

    this.type = urlSegments[1];
    const rawValue = urlSegments[2];
    let value: string;
    // this.authorityState = '';

    // Determine parameters based on URL structure
    if (this.type === 'city' && urlSegments[3]) {
      value = urlSegments[3];
      this.formattedValue = this.transformToCamelCase(value);
    } else if (this.type === 'authority' && urlSegments[4]) {
      value = rawValue;
      console.log('Authority URL:', urlSegments);
      this.authorityState = urlSegments[3];
      this.stateformatedauthority = this.transformToCamelCase(this.authorityState);
      this.formattedValue = this.transformToCamelCase(urlSegments[2]);
    } else {
      value = rawValue;
      this.formattedValue = this.transformToCamelCase(value);
    }

    this.loadTrendingDataCombined(this.type, value, this.authorityState);
  }

  private loadTrendingDataCombined(type: string, value: string, authorityState: string): void {
    // Check if data exists in TransferState
    const tendersKey = makeStateKey<any>('tendersData');
    const searchesKey = makeStateKey<any>('searchesData');

    const tendersFromState = this.transferState.hasKey(tendersKey) ? this.transferState.get(tendersKey, {}) : null;
    const searchesFromState = this.transferState.hasKey(searchesKey) ? this.transferState.get(searchesKey, {}) : null;

    if (tendersFromState && searchesFromState) {
      // Both data available in state
      this.handleStateData(tendersFromState, 'tenders');
      this.handleStateData(searchesFromState, 'searches');
      this.transferState.remove(tendersKey);
      this.transferState.remove(searchesKey);
      this.cdr.markForCheck();
    } else {
      // Fetch data from API using forkJoin for better performance
      this.isLoading = true;
      this.cdr.markForCheck();

      const tendersCall = this.sharedService.retrievetrending(type, value, authorityState);
      const searchesCall = this.sharedService.retrievetrendingSearches(type, value);

      forkJoin({
        tenders: tendersCall.pipe(catchError(() => of({ data: {} }))),
        searches: searchesCall.pipe(catchError(() => of({ data: [] })))
      })
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => {
            this.isLoading = false;
            this.cdr.markForCheck();
          })
        )
        .subscribe({
          next: (results) => {
            this.handleApiData(results.tenders?.data || {}, 'tenders', tendersKey);
            this.handleApiData(results.searches?.data || [], 'searches', searchesKey);
          },
          error: (error) => this.logError(error)
        });
    }
  }

  handleApiData(data: any, dataType: 'tenders' | 'searches', dataKey: any): void {
    if (dataType === 'tenders') {
      this.trendingStates = data.trendingStates || [];
      this.trendingCities = data.trendingCities || [];
      this.trendingKeywords = data.trendingKeywords || [];
      this.trendingAuthorities = data.trendingAuthorities || [];
    } else {
      this.trendingSearchList = data;
    }

    if (this.sharedService.isServer) {
      this.transferState.set(dataKey, data);
    }
  }

  handleStateData(data: any, dataType: 'tenders' | 'searches'): void {
    if (dataType === 'tenders') {
      this.trendingStates = data.trendingStates || [];
      this.trendingCities = data.trendingCities || [];
      this.trendingKeywords = data.trendingKeywords || [];
      this.trendingAuthorities = data.trendingAuthorities || [];
    } else {
      this.trendingSearchList = data || [];
    }
  }

  transformToCamelCase(value: string): string {
    const valueWithSpaces = value.replace(/-/g, ' ');
    return valueWithSpaces.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
  }

  private logError(error: any): void {
    // Only log errors in browser environment and development mode
    if (this.isBrowser && !environment.production) {
      console.error('TrendingSectionComponent Error:', error);
    }
  }

  /**
   * Navigate to tenders page with full page reload
   * This bypasses Angular's internal routing and triggers a full page reload
   * to pull fresh data from the CDN instead of using cached data
   */
  navigateToTenders(link: string): void {
    const url = this.type === 'authority'
      ? `/${link}/active`
      : `/${link}/active`;
    
    // Use window.location.href to reload the page and pull fresh data from CDN
    // This bypasses Angular's internal routing and triggers a full page reload
    window.location.href = url;
  }

  // navigateToTenders(link: string): void {
  //   this.router.navigate([`/${link}-tenders/active`]);
  // }

  navigate(event: MouseEvent, link: string): void {
    event.preventDefault();
    this.router.navigate([link]);
  }
}